import React, { useState } from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Sparkles, CheckCircle, ClipboardList, RefreshCcw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export default function Stocks() {
  const [isRequisitionOpen, setIsRequisitionOpen] = useState(false)
  const [isRequestStockOpen, setIsRequestStockOpen] = useState(false)
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false)

  return (
    <div className="p-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
            <div className="text-base font-semibold">Batch Details</div>
            <Badge variant="secondary" className="bg-gray-100 text-gray-600 border border-gray-200">Updated 2m ago</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="xs" onClick={() => setIsRequisitionOpen(true)}>
            <ClipboardList className="h-3.5 w-3.5 mr-1" />
            Create Requisition
          </Button>
          <Button variant="outline" size="xs" onClick={() => setIsRequestStockOpen(true)}>
            <RefreshCcw className="h-3.5 w-3.5 mr-1" />
            Request Stock
          </Button>
          <Button variant="outline" size="xs" onClick={() => setIsUpdateStatusOpen(true)}>
            <CheckCircle className="h-3.5 w-3.5 mr-1" />
            Update Status
          </Button>
        </div>
      </div>
      <div className="border-y border-gray-200">
      <Table className="text-xs">
        <TableHeader>
          <TableRow>
            <TableHead className="border-l border-gray-200">Batch Number</TableHead>
            <TableHead className="border-l border-gray-200">Date Received</TableHead>
            <TableHead className="border-l border-gray-200"><span className="inline-flex items-center gap-1">Days Left <Sparkles className="h-3 w-3 text-purple-500" /></span></TableHead>
            <TableHead className="border-l border-gray-200"><span className="inline-flex items-center gap-1">Quantity <Sparkles className="h-3 w-3 text-purple-500" /></span></TableHead>
            <TableHead className="border-l border-gray-200">Cost/Unit</TableHead>
            <TableHead className="border-l border-r border-gray-200">Cost/Unit</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="border-l border-gray-200">BAT-2024-001</TableCell>
            <TableCell className="border-l border-gray-200"><span className="inline-flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-green-600" /> 2025-01-15</span></TableCell>
            <TableCell className="border-l border-gray-200">305 days</TableCell>
            <TableCell className="border-l border-gray-200">500 units</TableCell>
            <TableCell className="border-l border-gray-200">AED 825.00</TableCell>
            <TableCell className="border-l border-r border-gray-200">AED 41.25</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="border-l border-gray-200">BAT-2024-002</TableCell>
            <TableCell className="border-l border-gray-200"><span className="inline-flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-green-600" /> 2025-02-20</span></TableCell>
            <TableCell className="border-l border-gray-200">270 days</TableCell>
            <TableCell className="border-l border-gray-200">300 units</TableCell>
            <TableCell className="border-l border-gray-200">AED 495.00</TableCell>
            <TableCell className="border-l border-r border-gray-200">AED 24.75</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="border-l border-gray-200">BAT-2024-003</TableCell>
            <TableCell className="border-l border-gray-200"><span className="inline-flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-green-600" /> 2024-12-10</span></TableCell>
            <TableCell className="border-l border-gray-200">135 days</TableCell>
            <TableCell className="border-l border-gray-200">250 units</TableCell>
            <TableCell className="border-l border-gray-200">AED 395.00</TableCell>
            <TableCell className="border-l border-r border-gray-200">AED 19.75</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      </div>

      {/* Create Requisition Sheet */}
      <Sheet open={isRequisitionOpen} onOpenChange={setIsRequisitionOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Create Requisition</SheetTitle>
            <SheetDescription>
              Create a new requisition for this batch.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="req-qty">Quantity</Label>
              <Input id="req-qty" type="number" placeholder="Enter quantity" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="req-priority">Priority</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="req-notes">Notes</Label>
              <Textarea id="req-notes" placeholder="Additional notes..." />
            </div>
          </div>
          <SheetFooter>
            <Button onClick={() => setIsRequisitionOpen(false)}>Create Requisition</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Request Stock Sheet */}
      <Sheet open={isRequestStockOpen} onOpenChange={setIsRequestStockOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Request Stock</SheetTitle>
            <SheetDescription>
              Request additional stock from the central warehouse.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="stock-qty">Quantity Needed</Label>
              <Input id="stock-qty" type="number" placeholder="Enter quantity" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="stock-reason">Reason</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="expired">Expired Stock</SelectItem>
                  <SelectItem value="damaged">Damaged Goods</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button onClick={() => setIsRequestStockOpen(false)}>Submit Request</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Update Status Sheet */}
      <Sheet open={isUpdateStatusOpen} onOpenChange={setIsUpdateStatusOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Update Status</SheetTitle>
            <SheetDescription>
              Update the status of this batch.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="status-val">New Status</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="quarantine">Quarantine</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                </SelectContent>
              </Select>
            </div>
             <div className="grid gap-2">
              <Label htmlFor="status-notes">Notes</Label>
              <Textarea id="status-notes" placeholder="Reason for status change..." />
            </div>
          </div>
          <SheetFooter>
            <Button onClick={() => setIsUpdateStatusOpen(false)}>Update Status</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  )
}
